// ChatWindow.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, setDoc, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, getDoc, getDocs, limit, deleteDoc, where } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ref as dbRef, onValue, onDisconnect, set, get } from 'firebase/database';
import { db, storage, database } from '../config/firebasedeconfig';
import '../styles/components/ChatWindow.scss';
import notificationService from '../services/notificationService';
import presenceService from '../services/presenceService';
import { defaultAvatarUrl } from '../constants';
import UserXpAvatar from './UserXpAvatar';
import { getLevelTitle, getDisplayActivityScore, getUserActivityScore } from './levelUtils';
import { useTranslation } from 'react-i18next';

// Émojis couramment utilisés
const commonEmojis = [
  '😀', '😂', '🥰', '😍', '😎', '🤔', '👍', '👌', '🙏', '🎉',
  '❤️', '🔥', '✨', '⭐', '🌟', '💯', '🤣', '😊', '😉', '😇'
];

// Durée maximale d'enregistrement audio en secondes
const MAX_AUDIO_DURATION = 120; // 2 minutes

// Configuration audio optimisée
const AUDIO_CONFIG = {
  maxDuration: 120, // 2 minutes max
  sampleRate: 44100,
  bitRate: 128000,
  timeslice: 100, // Capture toutes les 100ms pour plus de fluidité
  maxSize: 10 * 1024 * 1024, // 10MB max
  formats: ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
};

// Composant Pop-up personnalisé MovieFrenzy
const MovieFrenzyPopup = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  const logoUrl = "https://zupimages.net/up/24/40/4vcr.png"; // Logo MovieFrenzy

  return (
    <div className="moviefrenzy-popup-overlay">
      <div className="moviefrenzy-popup">
        <div className="popup-header">
          <img src={logoUrl} alt="MovieFrenzy" className="popup-logo" />
          <h3>{title}</h3>
        </div>
        <div className="popup-content">
          <p>{message}</p>
        </div>
        <div className="popup-actions">
          <button onClick={onClose} className="popup-button">
            Compris !
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook personnalisé pour l'enregistrement audio
const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  // Détecter le meilleur format audio supporté
  const getBestAudioFormat = useCallback(() => {
    for (const format of AUDIO_CONFIG.formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        return format;
      }
    }
    return 'audio/webm'; // Fallback
  }, []);

  // Démarrer l'enregistrement
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      chunksRef.current = [];

      // Demander l'accès au microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: AUDIO_CONFIG.sampleRate
        }
      });

      streamRef.current = stream;
      const mimeType = getBestAudioFormat();
      
      // Créer le MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: AUDIO_CONFIG.bitRate
      });

      mediaRecorderRef.current = mediaRecorder;

      // Gérer les données audio
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Gérer la fin d'enregistrement
      mediaRecorder.onstop = () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          if (blob.size > 100) { // Vérifier que l'enregistrement n'est pas vide
            setAudioBlob(blob);
            setAudioURL(URL.createObjectURL(blob));
          } else {
            setError("Enregistrement trop court ou vide");
          }
        }
        cleanup();
      };

      // Démarrer l'enregistrement
      mediaRecorder.start(AUDIO_CONFIG.timeslice);
      setIsRecording(true);

      // Démarrer le timer
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds++;
        setRecordingTime(seconds);
        
        // Arrêter automatiquement après la durée max
        if (seconds >= AUDIO_CONFIG.maxDuration) {
          stopRecording();
        }
      }, 1000);

    } catch (err) {
      console.error('[AUDIO] Erreur démarrage enregistrement:', err);
      setError(getErrorMessage(err));
      cleanup();
    }
  }, [getBestAudioFormat]);

  // Arrêter l'enregistrement
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  // Annuler l'enregistrement
  const cancelRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
    
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    
    setAudioBlob(null);
    setAudioURL(null);
    setRecordingTime(0);
    setError(null);
    cleanup();
  }, [isRecording, audioURL, stopRecording]);

  // Nettoyage des ressources
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Nettoyage à la fermeture du composant
  useEffect(() => {
    return () => {
      cleanup();
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [cleanup, audioURL]);

  // Messages d'erreur personnalisés
  const getErrorMessage = (err) => {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres.';
      case 'NotFoundError':
        return 'Aucun microphone détecté. Vérifiez votre équipement.';
      case 'NotSupportedError':
        return 'Enregistrement audio non supporté par ce navigateur.';
      default:
        return `Erreur microphone: ${err.message}`;
    }
  };

  return {
    isRecording,
    recordingTime,
    audioBlob,
    audioURL,
    error,
    startRecording,
    stopRecording,
    cancelRecording
  };
};

  const LEVEL_TITLE_FALLBACKS = {
    aspirant_figuring: "Aspirant Figurant",
    cadreur_novice: "Cadreur Novice",
    apprenti_monteur: "Apprenti Monteur",
    assistant_realisateur: "Assistant Réalisateur",
    scenariste_prometteur: "Scénariste Prometteur",
    chef_operateur_illumineur: "Chef Opérateur Illumineur",
    realisateur_emerite: "Réalisateur Émérite",
    maitre_mise_en_scene: "Maître de la Mise en Scène",
    legende_7e_art: "Légende du 7e Art",
    divinite_pellicule: "Divinité de la Pellicule",
  };


const ChatWindow = ({ currentUserId, peerUser, onClose }) => {
  const { t } = useTranslation();
  // Définition correcte de peerUserId
  const peerUserId = peerUser.userId || peerUser.uid;
  
  // Conversation ID stable (indépendant de l'ordre des IDs)
  const convId = [currentUserId, peerUserId].sort().join('_');
  const convRef = doc(db, 'conversations', convId);
  const messagesCol = collection(convRef, 'messages');
  
  // Log pour debug
  console.log("Chat Window - IDs utilisés:", { 
    currentUserId, 
    peerUserId, 
    peer_properties: Object.keys(peerUser),
    convId 
  });

  const [messages, setMessages] = useState([]);
  const [newText, setNewText] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [showFormatting, setShowFormatting] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMessageMenu, setSelectedMessageMenu] = useState(null);
  const [textFormat, setTextFormat] = useState({
    bold: false,
    italic: false,
    color: 'white'
  });
  
  // État pour le système de niveau
  const [peerUserMessageCount, setPeerUserMessageCount] = useState(0);
  
  // État pour le pop-up personnalisé
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({ title: '', message: '', type: 'info' });
  
  // État pour suivre si la conversation a été initialisée
  const [conversationInitialized, setConversationInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState(null);
  
  // États pour l'enregistrement audio (legacy - supprimés car remplacés par le hook)
  
  const bottomRef = useRef();
  const fileInputRef = useRef();
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const initAttemptsRef = useRef(0);

  // Ajouter une variable d'état pour contrôler le défilement automatique
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  // Hooks personnalisés
  const audioRecorder = useAudioRecorder();
  const peerActivityScore = getDisplayActivityScore(
    getUserActivityScore(peerUser || {}, peerUserMessageCount),
    peerUserId
  );

  const peerLevelKey = getLevelTitle(peerActivityScore);

  const peerLevelTitle = t(`forum.levelTitles.${peerLevelKey}`, {
    defaultValue: LEVEL_TITLE_FALLBACKS[peerLevelKey] || peerLevelKey,
  });
  // Fonction pour récupérer le messageCount du peer user
  const fetchPeerUserMessageCount = useCallback(async () => {
    if (!peerUserId) return;
    
    try {
      // Récupérer d'abord les données utilisateur
      const userDocRef = doc(db, 'users', peerUserId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Utiliser messageCount stocké dans le profil utilisateur ou calculer depuis les messages du forum
        let messageCount = userData.messageCount || 0;
        
        // Si pas de messageCount stocké, calculer depuis les posts du forum
        if (!messageCount) {
          const postsQuery = query(collection(db, 'posts'), where('authorId', '==', peerUserId));
          const postsSnapshot = await getDocs(postsQuery);
          messageCount = postsSnapshot.size;
          
          // Compter aussi les réponses dans les posts
          for (const postDoc of postsSnapshot.docs) {
            const repliesQuery = query(collection(db, 'posts', postDoc.id, 'replies'));
            const repliesSnapshot = await getDocs(repliesQuery);
            messageCount += repliesSnapshot.size;
          }
        }
        
        setPeerUserMessageCount(messageCount);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du messageCount du peer user:', error);
      setPeerUserMessageCount(0);
    }
  }, [peerUserId]);

  // Effet pour récupérer le messageCount du peer user
  useEffect(() => {
    fetchPeerUserMessageCount();
  }, [fetchPeerUserMessageCount]);

  // Initialisation de la conversation
  useEffect(() => {
    let unsubscribe = null;

    const initializeConversation = async () => {
      try {
        setIsInitializing(true);
        initAttemptsRef.current++;
        console.log(`[CHAT] 🟠 Tentative d'initialisation #${initAttemptsRef.current} pour ${convId}`);
        
        // Créer ou vérifier la conversation
        const convDoc = await getDoc(convRef);
        if (!convDoc.exists()) {
          console.log(`[CHAT] 🟠 Tentative création document conversation...`);
          await setDoc(convRef, {
            participants: [currentUserId, peerUserId],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            localTimestamp: Date.now(),
            lastMessage: ''
          });
          console.log(`[CHAT] 🟢 Document conversation créé avec succès`);
        }
        
        // Écouter les messages en temps réel
        const q = query(messagesCol, orderBy('createdAt'));
        unsubscribe = onSnapshot(q, (snapshot) => {
          const newMessages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`[CHAT] 🟢 ${newMessages.length} messages reçus`);
          setMessages(newMessages);
          setIsInitializing(false);
          setConversationInitialized(true);
          setInitError(null);
          
          // Scroll automatique vers le bas
          setTimeout(() => scrollToBottom(), 100);
        }, (err) => {
          console.error('Erreur écoute messages:', err);
          setInitError('Erreur de connexion aux messages');
          setIsInitializing(false);
        });

      } catch (err) {
        console.error('Erreur initialisation conversation:', err);
        setInitError('Impossible d\'initialiser la conversation');
        setIsInitializing(false);
        
        // Tentative de réessai automatique (max 3 fois)
        if (initAttemptsRef.current < 3) {
          console.log(`[CHAT] 🟠 Réessai dans 2 secondes...`);
          setTimeout(initializeConversation, 2000);
        }
      }
    };

    initializeConversation();

    return () => {
      if (unsubscribe) unsubscribe();
      // NOUVEAU: Nettoyage des timeouts de scroll
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentUserId, peerUserId, convId]);

  // Gestion de la présence utilisateur
  useEffect(() => {
    if (!currentUserId) return;
    
    console.log(`[CHAT] Initialisation du statut "chatting" pour l'utilisateur ${currentUserId}`);
    
    // Mettre à jour le statut pour indiquer une conversation en cours
    const cleanupPresence = presenceService.initializePresence(currentUserId, {
      status: 'chatting',
      conversationId: convId,
      chatWith: peerUserId,
      lastActive: new Date().toISOString()
    });
    
    return () => {
      console.log(`[CHAT] Nettoyage du statut pour l'utilisateur ${currentUserId}`);
      cleanupPresence();
      
      // Remettre le statut en ligne normal à la fermeture
      presenceService.updateUserStatus(currentUserId, 'online', {
        conversationId: null,
        chatWith: null,
        lastActive: new Date().toISOString()
      }).catch(err => {
        console.error("[CHAT] Erreur lors de la mise à jour du statut:", err);
      });
    };
  }, [currentUserId, peerUserId, convId]);

  // Gestion du menu de message
  const toggleMessageMenu = (msgId, event) => {
    if (event) event.stopPropagation();
    setSelectedMessageMenu(selectedMessageMenu === msgId ? null : msgId);
  };
  
  // Supprimer un message
  const deleteMessage = async (msgId) => {
    try {
      // Fermer le menu
      setSelectedMessageMenu(null);
      
      // Supprimer le message de Firestore
      const messageRef = doc(messagesCol, msgId);
      await deleteDoc(messageRef);
      
      console.log(`Message ${msgId} supprimé avec succès`);
    } catch (error) {
      console.error("Erreur lors de la suppression du message:", error);
      alert("Erreur lors de la suppression du message. Veuillez réessayer.");
    }
  };
  
  // Transférer un message
  const forwardMessage = (msg) => {
    // Fermer le menu
    setSelectedMessageMenu(null);
    
    // Stocker le message à transférer dans localStorage pour accès depuis EmuleProfil ou autre
    const forwardData = {
      type: msg.type,
      content: msg.type === 'text' ? msg.text : 
               msg.type === 'image' ? msg.imageUrl : 
               msg.type === 'audio' ? msg.audioUrl : '',
      timestamp: new Date().getTime()
    };
    
    localStorage.setItem('forwardMessage', JSON.stringify(forwardData));
    
    // Afficher le pop-up personnalisé MovieFrenzy
    setPopupData({
      title: 'Message prêt à transférer',
      message: 'Votre message est maintenant prêt à être transféré ! Ouvrez une autre conversation pour le partager avec un autre utilisateur.',
      type: 'success'
    });
    setShowPopup(true);
  };

  // AMÉLIORATION: Fonction optimisée pour gérer le scroll intelligent
  const scrollToBottom = useCallback((force = false) => {
    if (!messagesEndRef.current) return;
    
    // Scroll forcé (nouveau message de l'utilisateur actuel) ou scroll normal si autorisé
    if (force || (shouldScrollToBottom && !isUserScrolling)) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'end',
        inline: 'nearest'
      });
    }
  }, [shouldScrollToBottom, isUserScrolling]);

  // Effet pour scroll automatique quand de nouveaux messages arrivent
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // AMÉLIORATION: Fonction optimisée pour détecter l'interaction utilisateur avec le scroll
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Calculer la distance depuis le bas
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 100;
    
    // Détecter si l'utilisateur scrolle manuellement vers le haut
    if (distanceFromBottom > 150) {
      setIsUserScrolling(true);
      setShouldScrollToBottom(false);
      
      // Réinitialiser après une période d'inactivité
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 3000); // 3 secondes d'inactivité
    } else if (isNearBottom) {
      // L'utilisateur est revenu vers le bas
      setIsUserScrolling(false);
      setShouldScrollToBottom(true);
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    }
  }, []);

  // Ajouter une fonction pour effacer toute la conversation
  const deleteAllMessages = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer toute la conversation ? Cette action est irréversible.")) {
      return;
    }
    
    try {
      // Récupérer tous les messages
      const q = query(messagesCol);
      const snapshot = await getDocs(q);
      
      // Supprimer chaque message
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Mettre à jour la conversation
      await updateDoc(convRef, {
        lastMessage: "Conversation effacée",
        updatedAt: serverTimestamp()
      });
      
      console.log("Tous les messages ont été supprimés");
    } catch (error) {
      console.error("Erreur lors de la suppression des messages:", error);
      alert("Une erreur est survenue lors de la suppression des messages.");
    }
  };

  // Envoyer un message
  const sendMessage = async (messageType = 'text', content = newText, format = textFormat) => {
    // Vérifier si les conditions de base sont remplies pour envoyer un message
    if ((messageType === 'text' && !content.trim()) || isUploading) {
      console.log("[CHAT] Message vide ou upload en cours, abandon de l'envoi");
      return;
    }
    
    // Vérifier que la conversation est initialisée
    if (!conversationInitialized) {
      console.log("[CHAT] Tentative d'envoi de message alors que la conversation n'est pas initialisée");
      try {
        // Vérification rapide et création si nécessaire
        const snap = await getDoc(convRef);
        
        if (!snap.exists()) {
          console.log("[CHAT] Création d'urgence de la conversation");
          await setDoc(convRef, {
            participants: [currentUserId, peerUserId],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastMessage: ''
          });
        }
        
        setConversationInitialized(true);
        console.log("[CHAT] Conversation initialisée avec succès - poursuivre l'envoi");
      } catch (err) {
        console.error("[CHAT] Échec critique d'initialisation:", err);
        alert("Impossible d'envoyer votre message. Veuillez fermer et réouvrir la conversation.");
        return;
      }
    }
    
    console.log("Tentative d'envoi de message:", messageType, content ? content.substring(0, 20) : 'empty');
    
    try {
      const messageData = {
        from: currentUserId,
        type: messageType,
        createdAt: serverTimestamp()
      };
      
      // Ajouter le contenu en fonction du type de message
      if (messageType === 'text') {
        messageData.text = content.trim();
        messageData.format = format;
      } else if (messageType === 'image') {
        messageData.imageUrl = content;
      } else if (messageType === 'audio') {
        messageData.audioUrl = content;
        messageData.duration = audioRecorder.recordingTime;
      } else if (messageType === 'gif') {
        messageData.gifUrl = content;
      }
      
      console.log("Message à envoyer:", messageData);
      
      // Vérifier que le message est valide avant d'essayer de l'envoyer
      if ((messageType === 'text' && !messageData.text) || 
          (messageType === 'image' && !messageData.imageUrl) || 
          (messageType === 'audio' && !messageData.audioUrl) ||
          (messageType === 'gif' && !messageData.gifUrl)) {
        console.error("Message invalide, abandon de l'envoi");
        return;
      }
      
      // Envoyer le message à Firestore
      const docRef = await addDoc(messagesCol, messageData);
      console.log("Message envoyé avec succès, ID:", docRef.id);
      
      // Mettre à jour le dernier message
      let lastMessageText;
      if (messageType === 'text') {
        lastMessageText = content.substring(0, 50); // Limiter la longueur pour le résumé
      } else if (messageType === 'image') {
        lastMessageText = '📷 Image';
      } else if (messageType === 'audio') {
        lastMessageText = '🎤 Message vocal';
      } else if (messageType === 'gif') {
        lastMessageText = '🎬 GIF';
      }
      
      await updateDoc(convRef, {
        lastMessage: lastMessageText,
        updatedAt: serverTimestamp()
      });
      
      // Création d'une notification pour le destinataire
      try {
        // Récupérer les informations de l'utilisateur actuel
        const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
        const currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};
        const senderName = currentUserData.username || 'Utilisateur';
        
        // Créer une notification pour le destinataire
        await notificationService.createMessageNotification(
          peerUserId,
          currentUserId,
          senderName
        );
      } catch (notifError) {
        console.error("Erreur lors de la création de la notification:", notifError);
      }
      
      if (messageType === 'text') {
        setNewText('');
      } else if (messageType === 'audio') {
        // Réinitialiser l'audio après envoi
        audioRecorder.cancelRecording();
      } else if (messageType === 'gif') {
        setShowGifPicker(false);
      }
      
      // Activer le défilement automatique après l'envoi d'un message
      setShouldScrollToBottom(true);
      setIsUserScrolling(false);
      setTimeout(() => {
        scrollToBottom(true);
      }, 100);
      
    } catch (err) {
      console.error("Erreur envoi message :", err);
      alert("Erreur lors de l'envoi du message. Veuillez réessayer.");
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    const fileType = file.type;
    if (!fileType.startsWith('image/')) {
      alert('Seules les images sont acceptées pour le moment');
      return;
    }
    
    // Limiter la taille à 5MB
    if (file.size > 5 * 1024 * 1024) {
      alert('Le fichier est trop volumineux (max 5MB)');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Redimensionner l'image avant l'upload
      const resizedImage = await resizeImage(file, 1200); // max 1200px de largeur
      
      // Créer une référence unique pour le fichier
      const storageRef = ref(storage, `chat-images/${convId}/${Date.now()}_${file.name}`);
      
      // Démarrer l'upload
      const uploadTask = uploadBytesResumable(storageRef, resizedImage);
      
      // Surveiller la progression
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Erreur upload:', error);
          setIsUploading(false);
          alert('Erreur lors de l\'upload. Veuillez réessayer.');
        },
        async () => {
          // Terminé avec succès
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await sendMessage('image', downloadURL);
          setIsUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (err) {
      console.error('Erreur initiation upload:', err);
      setIsUploading(false);
    }
  };
  
  // Fonction pour redimensionner l'image
  const resizeImage = (file, maxWidth) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calculer les nouvelles dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          // Créer un canvas pour redimensionner
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Dessiner l'image redimensionnée
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir en Blob avec une qualité réduite (0.85)
          canvas.toBlob((blob) => {
            resolve(blob);
          }, file.type, 0.85);
        };
      };
    });
  };
  
  const uploadAudio = async (blob) => {
    try {
      // Vérifier que le blob audio est valide
      if (!blob || blob.size === 0) {
        console.error("[AUDIO] Blob audio invalide ou vide");
        alert("Enregistrement audio invalide. Veuillez réessayer.");
        return;
      }
      
      // Taille maximale autorisée (10 Mo)
      const MAX_SIZE = 10 * 1024 * 1024; 
      if (blob.size > MAX_SIZE) {
        console.error(`[AUDIO] Blob audio trop volumineux: ${blob.size} octets`);
        alert("L'enregistrement audio est trop volumineux. La durée maximale est de 2 minutes.");
        return;
      }
      
      setIsUploading(true);
      setUploadProgress(0);
      
      console.log(`[AUDIO] Début de l'upload (taille: ${blob.size} octets)`);
      
      // AMÉLIORATION: Vérifier l'authentification avant l'upload
      if (!currentUserId) {
        console.error("[AUDIO] Utilisateur non authentifié");
        alert("Erreur d'authentification. Veuillez vous reconnecter.");
        setIsUploading(false);
        return;
      }
      
      // Créer une référence unique pour le fichier audio
      const filename = `audio_${Date.now()}.webm`;
      const storageRef = ref(storage, `chat-audio/${convId}/${filename}`);
      
      // AMÉLIORATION: Ajouter des métadonnées pour contourner certaines restrictions
      const metadata = {
        contentType: 'audio/webm',
        customMetadata: {
          'uploadedBy': currentUserId,
          'conversationId': convId,
          'timestamp': Date.now().toString()
        }
      };
      
      // Démarrer l'upload avec métadonnées
      const uploadTask = uploadBytesResumable(storageRef, blob, metadata);
      
      // Surveiller la progression
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          console.log(`[AUDIO] Progression: ${Math.round(progress)}%`);
        },
        (error) => {
          console.error('[AUDIO] Erreur upload:', error);
          setIsUploading(false);
          
          // AMÉLIORATION: Messages d'erreur plus spécifiques et solutions
          if (error.code === 'storage/unauthorized') {
            alert(`Erreur d'autorisation Storage. Solutions possibles:
            
1. Vérifiez que Firebase Storage est activé dans votre projet
2. Vérifiez les restrictions d'API dans Google Cloud Console
3. Assurez-vous que votre clé API autorise 'Cloud Storage API'
4. Les règles de sécurité Storage doivent être déployées

Code d'erreur: ${error.code}`);
          } else if (error.code === 'storage/canceled') {
            alert("L'upload a été annulé. Veuillez réessayer.");
          } else if (error.code === 'storage/quota-exceeded') {
            alert("L'espace de stockage est plein. Contactez l'administrateur.");
          } else if (error.code === 'storage/retry-limit-exceeded') {
            alert("Trop de tentatives. Vérifiez votre connexion internet.");
          } else {
            alert(`Erreur lors de l'upload audio: ${error.message}
            
Code d'erreur: ${error.code}
            
Veuillez vérifier:
- Votre connexion internet
- Les paramètres Firebase de votre projet
- Les restrictions d'API dans Google Cloud Console`);
          }
        },
        async () => {
          // Terminé avec succès
          console.log("[AUDIO] Upload terminé avec succès");
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            await sendMessage('audio', downloadURL);
            console.log("[AUDIO] Message audio envoyé avec succès");
          } catch (urlError) {
            console.error("[AUDIO] Erreur lors de la récupération de l'URL:", urlError);
            alert("Erreur lors de la finalisation de l'envoi. Veuillez réessayer.");
          } finally {
            setIsUploading(false);
            setUploadProgress(0);
          }
        }
      );
    } catch (err) {
      console.error('[AUDIO] Erreur critique:', err);
      setIsUploading(false);
      alert(`Erreur lors de l'initialisation de l'upload audio: ${err.message}. Veuillez réessayer.`);
    }
  };
  
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  
  const addEmoji = (emoji) => {
    setNewText(prev => prev + emoji);
    setShowEmojis(false);
  };
  
  const toggleFormat = (format) => {
    setTextFormat(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
  };
  
  const changeColor = (color) => {
    setTextFormat(prev => ({
      ...prev,
      color
    }));
  };
  
  const getMessageStyle = (format) => {
    if (!format) return {};
    
    return {
      fontWeight: format.bold ? 'bold' : 'normal',
      fontStyle: format.italic ? 'italic' : 'normal',
      color: format.color || 'white'
    };
  };

  // Fonction spécifique pour tenter une réinitialisation manuelle
  const handleRetryInitialization = () => {
    setInitError(null);
    setIsInitializing(true);
    initAttemptsRef.current = 0; // Réinitialiser le compteur
    
    // Force un nouveau cycle d'initialisation en créant une version 
    // temporaire aléatoire du convId que nous utiliserons
    const tempConvId = convId + '_retry_' + Date.now();
    
    console.log(`[CHAT] 🔄 Tentative de réinitialisation manuelle avec ID temporaire: ${tempConvId}`);
    
    // Approche corrective: tenter d'utiliser une autre méthode pour établir la connexion
    const tempConvRef = doc(db, 'conversations', tempConvId);
    
    // Solution temporaire: essayer de créer un document avec une référence temporaire
    setDoc(tempConvRef, {
      isTemporary: true,
      originalConvId: convId,
      participants: [currentUserId, peerUserId],
      createdAt: serverTimestamp(),
      testTimestamp: Date.now()
    })
    .then(() => {
      console.log(`[CHAT] ✅ Document temporaire créé avec succès`);
      
      // Si cette opération réussit, supprimer le document temporaire puis réessayer
      // d'initialiser la vraie conversation
      setTimeout(() => {
        deleteDoc(tempConvRef)
          .then(() => {
            console.log(`[CHAT] 🔄 Test réussi, nouvelle tentative sur la conversation principale`);
            // Forcer un nouveau rendu en modifiant l'état
            setConversationInitialized(false);
            
            // Appliquer un délai court avant de réinitialiser l'état complet
            setTimeout(() => {
              window.location.reload(); // Solution de dernier recours
            }, 1000);
          })
          .catch(err => {
            console.error(`[CHAT] ❌ Échec nettoyage document temporaire:`, err);
            setInitError("Échec de la réinitialisation. Veuillez actualiser la page.");
            setIsInitializing(false);
          });
      }, 500);
    })
    .catch(err => {
      console.error(`[CHAT] ❌ Échec création document temporaire:`, err);
      setInitError(`Problème persistant: ${err.message}. Vérifiez votre connexion.`);
      setIsInitializing(false);
    });
  };

  // AMÉLIORATION: Fonction optimisée pour gérer la sélection de GIF
  const handleGifSelect = (gifUrl) => {
    setShowGifPicker(false); // Fermer le panneau immédiatement
    sendMessage('gif', gifUrl);
  };

  // NOUVEAU: Liste étendue de GIFs populaires organisés par catégorie
  const gifCategories = {
    rire: [
      'https://media.giphy.com/media/3o7aCTfyhYawdOXcFW/giphy.gif',
      'https://media.giphy.com/media/l0HlvtIPzPdt2usKs/giphy.gif',
      'https://media.giphy.com/media/l3q2K5jinAlChoCLS/giphy.gif',
      'https://media.giphy.com/media/T3Vx6sVAXzuG4/giphy.gif'
    ],
    love: [
      'https://media.giphy.com/media/26BRBupa6nRXMGBP2/giphy.gif',
      'https://media.giphy.com/media/3o6Zt6KHxJTbXCnSvu/giphy.gif',
      'https://media.giphy.com/media/l0MYGb1LuZ3n7dRnO/giphy.gif',
      'https://media.giphy.com/media/26BROrSHlmyzzHf3i/giphy.gif'
    ],
    reaction: [
      'https://media.giphy.com/media/26ufdipQqU2lhNA4g/giphy.gif',
      'https://media.giphy.com/media/5VKbvrjxpVJCM/giphy.gif',
      'https://media.giphy.com/media/3o7527pa7qs9kCG78A/giphy.gif',
      'https://media.giphy.com/media/l0MYC0LajbaPoEADu/giphy.gif'
    ],
    celebration: [
      'https://media.giphy.com/media/l0MYGb1LuZ3n7dRnO/giphy.gif',
      'https://media.giphy.com/media/3o6fJ1BM7R2EBRDnxK/giphy.gif',
      'https://media.giphy.com/media/l0K4puBUN5gB8nJFm/giphy.gif',
      'https://media.giphy.com/media/3o7527pa7qs9kCG78A/giphy.gif'
    ]
  };

  if (initError && !messages.length) {
    return (
      <div className="chat-window">
        <div className="chat-header">
          <span>Erreur de connexion</span>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="chat-error">
          <p>{initError}</p>
          <button onClick={handleRetryInitialization}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-user-info">
          <div className="chat-avatar-container">
            <UserXpAvatar
              avatarUrl={peerUser.photoURL || peerUser.avatarUrl || peerUser.avatar || defaultAvatarUrl}
              activityScore={peerActivityScore}
              fallbackActivityScore={peerActivityScore}
              showTitle={false}
              showActivity={false}
              className="chat-window-xp-avatar"
              userId={peerUserId}
            />
          </div>
          <div className="chat-username-container">
            <div className="chat-username">
              {peerUser.displayName || peerUser.username || 'Utilisateur'}
            </div>
            <div className="chat-user-level">
              {peerLevelTitle} • {peerUserMessageCount} messages
            </div>
          </div>
        </div>
        <div className="chat-actions">
          <button 
            className="delete-all-button" 
            onClick={deleteAllMessages}
            title="Effacer toute la conversation"
          >
            🗑️
          </button>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="chat-messages" onScroll={handleScroll}>
        {isInitializing && messages.length === 0 && (
          <div className="loading-messages">
            <p>Chargement de la conversation...</p>
          </div>
        )}
        
        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`chat-message ${msg.from === currentUserId ? 'from-me' : ''}`}
          >
            {/* Nom de l'auteur avec horodatage */}
            {msg.from !== currentUserId && (
              <div className="message-author" data-time={msg.createdAt ? new Date(msg.createdAt.toDate()).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              }) : ''}>
                {peerUser.displayName || peerUser.username || 'Utilisateur'}
              </div>
            )}
            
            {/* Menu à trois points pour les options */}
            <div className="message-options">
              <button 
                title="Options" 
                onClick={(e) => toggleMessageMenu(msg.id, e)}
                className="message-options-button"
              >
                ⋮
              </button>
              
              {selectedMessageMenu === msg.id && (
                <div className="options-dropdown">
                  <ul>
                    <li onClick={() => forwardMessage(msg)}>Transférer</li>
                    <li onClick={() => deleteMessage(msg.id)}>Supprimer</li>
                  </ul>
                </div>
              )}
            </div>
            
            {msg.type === 'image' ? (
              <div className="image-container">
                <img src={msg.imageUrl} alt="Image partagée" className="shared-image" />
              </div>
            ) : msg.type === 'audio' ? (
              <div className="audio-message">
                <div className="audio-wave-icon">🎵</div>
                <audio 
                  controls 
                  src={msg.audioUrl} 
                  className="audio-player"
                />
                {msg.duration && (
                  <span className="audio-duration">
                    {formatTime(msg.duration)}
                  </span>
                )}
              </div>
            ) : msg.type === 'gif' ? (
              <div className="gif-container">
                <img src={msg.gifUrl} alt="GIF" className="shared-gif" />
              </div>
            ) : (
              <span style={getMessageStyle(msg.format)}>
                {msg.text}
              </span>
            )}
            
            {/* Horodatage du message */}
            <div className="message-timestamp">
              {msg.createdAt ? new Date(msg.createdAt.toDate()).toLocaleString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: '2-digit'
              }) : ''}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="chat-toolbar">
          <button 
            className="toolbar-button"
            onClick={() => setShowEmojis(!showEmojis)}
            title="Émojis"
          >
            😀
          </button>
          
          <button 
            className="toolbar-button"
            onClick={() => fileInputRef.current.click()}
            title="Envoyer une image"
            disabled={isUploading || audioRecorder.isRecording || audioRecorder.audioURL}
          >
            📎
          </button>
          
          <input 
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileUpload}
          />
          
          <button 
            className="toolbar-button"
            onClick={() => setShowFormatting(!showFormatting)}
            title="Options de texte"
          >
            🎨
          </button>
          
          <button 
            className="toolbar-button"
            onClick={() => setShowGifPicker(!showGifPicker)}
            title="Envoyer un GIF"
            disabled={isUploading || audioRecorder.isRecording || audioRecorder.audioURL}
          >
            🎬
          </button>
          
          <button 
            className={`toolbar-button ${audioRecorder.isRecording ? 'recording-active' : ''}`}
            onClick={audioRecorder.isRecording ? audioRecorder.stopRecording : audioRecorder.startRecording}
            title={audioRecorder.isRecording ? "Arrêter l'enregistrement" : "Enregistrer un message vocal"}
            disabled={audioRecorder.audioURL || isUploading}
            style={{ color: audioRecorder.isRecording ? '#FF3333' : 'white' }}
          >
            {!audioRecorder.isRecording ? '🎤' : '⏹️'}
          </button>
          
          {isUploading && (
            <div className="upload-progress">
              <div 
                className="progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
              <span className="progress-text">{Math.round(uploadProgress)}%</span>
            </div>
          )}
        </div>
        
        {/* Interface d'enregistrement audio */}
        {audioRecorder.audioURL ? (
          <div className="audio-recorder">
            <div className="audio-preview">
              <audio 
                ref={audioRef}
                controls 
                src={audioRecorder.audioURL} 
                className="audio-preview-player"
              />
              <div className="audio-preview-controls">
                <button 
                  className="cancel-audio-button"
                  onClick={audioRecorder.cancelRecording}
                >
                  Annuler
                </button>
                <button 
                  className="send-audio-button"
                  onClick={() => {
                    if (audioRecorder.audioBlob) {
                      uploadAudio(audioRecorder.audioBlob);
                    }
                  }}
                  disabled={isUploading}
                >
                  {isUploading ? 'Envoi en cours...' : 'Envoyer'}
                </button>
              </div>
            </div>
          </div>
        ) : audioRecorder.isRecording ? (
          <div className="audio-recorder">
            <div className="recording-status">
              <div className="recording-indicator"></div>
              <span className="recording-time">{formatTime(audioRecorder.recordingTime)}</span>
              <button 
                className="stop-recording-button"
                onClick={audioRecorder.stopRecording}
                title="Arrêter l'enregistrement"
              >
                ⏹️
              </button>
              <button 
                className="cancel-recording-button"
                onClick={audioRecorder.cancelRecording}
                title="Annuler l'enregistrement"
              >
                ❌
              </button>
            </div>
          </div>
        ) : null}
        
        {/* Panneau d'émojis */}
        {showEmojis && (
          <div className="emoji-picker">
            {commonEmojis.map((emoji, index) => (
              <button 
                key={index} 
                className="emoji-button"
                onClick={() => {
                  setNewText(prev => prev + emoji);
                  setShowEmojis(false);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        
        {/* Options de formatage */}
        {showFormatting && (
          <div className="format-options">
            <button 
              className={`format-button ${textFormat.bold ? 'active' : ''}`}
              onClick={() => toggleFormat('bold')}
              title="Gras"
            >
              B
            </button>
            <button 
              className={`format-button ${textFormat.italic ? 'active' : ''}`}
              onClick={() => toggleFormat('italic')}
              title="Italique"
            >
              I
            </button>
            <div className="color-options">
              {['white', '#FF33CC', '#33CCFF', '#FFCC33', '#66FF66'].map(color => (
                <button 
                  key={color}
                  className={`color-button ${textFormat.color === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => changeColor(color)}
                ></button>
              ))}
            </div>
          </div>
        )}
        
        {/* AMÉLIORATION: Panneau de sélection de GIFs avec catégories organisées */}
        {showGifPicker && (
          <div className="gif-picker">
            <div className="gif-search">
              <input 
                type="text" 
                placeholder="Rechercher un GIF..." 
                className="gif-search-input"
              />
            </div>
            <div className="gif-categories">
              <button onClick={() => setShowGifPicker(false)} className="close-gif-picker">✕</button>
              <button onClick={() => gifCategories.rire.forEach((gif, i) => i === 0 && handleGifSelect(gif))}>😂 Rire</button>
              <button onClick={() => gifCategories.love.forEach((gif, i) => i === 0 && handleGifSelect(gif))}>❤️ Love</button>
              <button onClick={() => gifCategories.reaction.forEach((gif, i) => i === 0 && handleGifSelect(gif))}>😮 Réaction</button>
              <button onClick={() => gifCategories.celebration.forEach((gif, i) => i === 0 && handleGifSelect(gif))}>🎉 Fête</button>
            </div>
            <div className="gif-grid">
              {/* Affichage de tous les GIFs par catégorie */}
              {Object.entries(gifCategories).map(([category, gifs]) => 
                gifs.map((gifUrl, index) => (
                  <img 
                    key={`${category}-${index}`}
                    src={gifUrl} 
                    alt={`GIF ${category}`} 
                    className="gif-thumbnail"
                    onClick={() => handleGifSelect(gifUrl)}
                    loading="lazy"
                  />
                ))
              )}
            </div>
          </div>
        )}
        
        <div className="chat-input-area">
          <input
            type="text"
            value={newText}
            onChange={e => setNewText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Tapez votre message…"
            style={getMessageStyle(textFormat)}
            disabled={audioRecorder.isRecording || audioRecorder.audioURL}
          />
          <button 
            onClick={() => sendMessage()} 
            disabled={!newText.trim() || isUploading || audioRecorder.isRecording || audioRecorder.audioURL}
          >
            Envoyer
          </button>
        </div>
      </div>

      {/* Pop-up personnalisé MovieFrenzy */}
      <MovieFrenzyPopup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        title={popupData.title}
        message={popupData.message}
        type={popupData.type}
      />
    </div>
  );
};

export default ChatWindow;

